import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Container,
  Grid,
  MenuItem,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { apiTenant } from '../../../services/Tenant/ApiTenant'
import TenantStatusBadge from '../../../components/Tenant/TenantStatusBadge'
import Lottie from 'lottie-react'
import emptyBox from '../../../assets/animations/emptyBox.json'
import { grey } from '@mui/material/colors'
import moment from 'moment'

const formatBRL = (centavos) => {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const currentYear = new Date().getFullYear()
const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

const TenantInvoiceList = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    year: '',
    ordering: '-due_date',
  })

  const toArray = (data) => {
    if (Array.isArray(data)) return data
    if (data?.results && Array.isArray(data.results)) return data.results
    return []
  }

  useEffect(() => {
    setLoading(true)
    const params = {}
    if (filters.status) params.status = filters.status
    if (filters.year) params.year = filters.year
    if (filters.ordering) params.ordering = filters.ordering

    apiTenant.get('/tenant/invoices/', { params })
      .then(response => {
        setInvoices(toArray(response.data))
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filters])

  const monthNames = [
    '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]

  return (
    <>
      <Helmet>
        <title>Faturas - Portal do Cliente</title>
      </Helmet>
      <Container maxWidth={false}>
        <Box sx={{ marginY: '1em', pb: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h5">Faturas</Typography>
        </Box>

        <Card>
          <Grid container sx={{ p: 2, alignItems: 'center' }} spacing={2}>
            <Grid item xs={12} sm={6} lg={4}>
              <ToggleButtonGroup
                fullWidth
                exclusive
                value={filters.status}
                color="primary"
                onChange={(e, value) => value !== null && setFilters(prev => ({ ...prev, status: value }))}
                size="small"
              >
                <ToggleButton value="">Todas</ToggleButton>
                <ToggleButton value="pending">Pendentes</ToggleButton>
                <ToggleButton value="overdue">Atrasadas</ToggleButton>
                <ToggleButton value="paid">Pagas</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={6} sm={3} lg={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Ano"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {yearOptions.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} lg={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Ordenar"
                value={filters.ordering}
                onChange={(e) => setFilters(prev => ({ ...prev, ordering: e.target.value }))}
              >
                <MenuItem value="-due_date">Mais recentes</MenuItem>
                <MenuItem value="due_date">Mais antigas</MenuItem>
                <MenuItem value="-total">Maior valor</MenuItem>
                <MenuItem value="total">Menor valor</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {loading ? (
            <Skeleton variant="rounded" height={400} animation="pulse" sx={{ margin: 1 }}/>
          ) : invoices?.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'primary.main' }}>Nº da Fatura</TableCell>
                    <TableCell sx={{ color: 'primary.main' }}>Imóvel</TableCell>
                    <TableCell align="center" sx={{ color: 'primary.main' }}>Referência</TableCell>
                    <TableCell align="center" sx={{ color: 'primary.main' }}>Vencimento</TableCell>
                    <TableCell align="center" sx={{ color: 'primary.main' }}>Status</TableCell>
                    <TableCell sx={{ color: 'primary.main' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(invoice => (
                    <TableRow
                      key={invoice.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/area-cliente/faturas/${invoice.id}`)}
                    >
                      <TableCell>{invoice.number}</TableCell>
                      <TableCell>Unidade {invoice.unit_number}</TableCell>
                      <TableCell align="center">
                        {invoice.month && invoice.year
                          ? `${monthNames[invoice.month]}/${invoice.year}`
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {invoice.due_date ? moment(invoice.due_date).format('DD/MM/YYYY') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <TenantStatusBadge status={invoice.status}/>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatBRL(invoice.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', py: 4 }}>
              <Typography
                align="center"
                sx={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '24px', fontWeight: 'bold', color: grey[400] }}
              >
                Nenhum Resultado!
              </Typography>
              <Box sx={{ width: '250px' }}>
                <Lottie animationData={emptyBox} loop={true}/>
              </Box>
            </Box>
          )}
        </Card>
      </Container>
    </>
  )
}

export default TenantInvoiceList
